import React, { useState } from "react";
import { Download, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { PARTNERSHIP_CATEGORIES, INQUIRY_TYPES } from "../../data/content";
import api from "../../lib/api";
import { toast } from "sonner";

export const Partnerships = ({ settings }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "", partnership_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", form);
      toast.success(data.message || "Thanks for reaching out.");
      setForm({ name: "", company: "", email: "", partnership_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="partnerships" data-testid="partnerships-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-matte">
      <div className="grid md:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <Reveal>
            <p className="eyebrow mb-4">Partnerships & Media</p>
            <h2 className="heading text-5xl sm:text-6xl lg:text-7xl text-cream mb-8">
              Let's Build Something Real.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-cream/75 leading-relaxed mb-10">
              <p>I'm grateful to have built a community that supports more than basketball. They support the journey.</p>
              <p>Whether it's a brand partnership, an interview, a podcast, or a public speaking engagement, I love collaborating with people who believe in hard work, authenticity, and doing things your own way.</p>
              <p>Because the best partnerships don't feel like ads. They feel real.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border-l-2 border-stone/60 pl-5 mb-10">
              <p className="text-[0.65rem] uppercase tracking-[0.3em] text-silver/50 mb-1">Representation</p>
              <p className="text-cream/90">
                Jaron is represented by <span className="text-stone font-semibold">Blue Birds Sports</span> for media &amp; partnerships.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3 mb-10">
              {PARTNERSHIP_CATEGORIES.map((c) => (
                <span key={c} className="border border-silver/20 px-4 py-2 text-xs uppercase tracking-[0.15em] text-cream/70">
                  {c}
                </span>
              ))}
            </div>
            <a
              data-testid="media-kit-btn"
              href="/media/JaRon-Thames-Media-Kit.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <Download size={16} /> Download Media Kit
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-silver/15 bg-charcoal/30 p-8 md:p-10 space-y-6">
            <h3 className="heading text-3xl text-cream mb-2">Partnership &amp; Media Inquiry</h3>
            <input data-testid="inq-name" required placeholder="Name" value={form.name} onChange={set("name")} className="field" />
            <input data-testid="inq-company" placeholder="Company / Outlet" value={form.company} onChange={set("company")} className="field" />
            <input data-testid="inq-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")} className="field" />
            <select data-testid="inq-type" value={form.partnership_type} onChange={set("partnership_type")} className="field text-cream">
              <option value="" className="bg-matte">Inquiry Type</option>
              {INQUIRY_TYPES.map((c) => (
                <option key={c} value={c} className="bg-matte">{c}</option>
              ))}
            </select>
            <textarea data-testid="inq-message" required placeholder="Message" rows={4} value={form.message} onChange={set("message")} className="field resize-none" />
            <button data-testid="inq-submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Sending…" : <>Send Inquiry <Send size={15} /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
